import { useCallback, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  endpoints,
  sha256Hex,
  APIError,
  type BatchItem,
  type BatchResponse,
  type UploadResponse,
} from "@/lib/api";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { Table, THead, TBody, TR, TH, TD } from "@/components/ui/Table";
import { cn } from "@/lib/utils";

// ---------------------------------------------------------------------------
// CSV Import — Day 22. Drag-drop a CSV / paste content → POST raw body to
// /api/csv/upload, then poll /api/csv/batches?by_file_hash=... until the
// batch row appears, then poll the batch row + items until terminal.
//
// SPEC §16: max 200 rows; supports CN/EN headers; same file hash dedups.
// ---------------------------------------------------------------------------

type Phase =
  | { kind: "idle" }
  | { kind: "uploading" }
  | { kind: "queued"; resp: UploadResponse; hash: string }
  | { kind: "processing"; batchID: number; hash: string }
  | { kind: "done"; batchID: number; hash: string }
  | { kind: "error"; message: string };

export default function CSVImport() {
  const [phase, setPhase] = useState<Phase>({ kind: "idle" });
  const [fileName, setFileName] = useState("");
  const [batchName, setBatchName] = useState("");
  const [dragging, setDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const onSubmit = useCallback(async (body: string, filename: string) => {
    if (!body.trim()) {
      setPhase({ kind: "error", message: "CSV 内容为空" });
      return;
    }
    setPhase({ kind: "uploading" });
    try {
      const hash = await sha256Hex(body);
      const resp = await endpoints.csvUpload(body, {
        name: batchName.trim() || undefined,
        filename: filename || undefined,
      });
      setPhase({ kind: "queued", resp, hash });
    } catch (err) {
      const msg = err instanceof APIError ? err.message : (err as Error).message;
      setPhase({ kind: "error", message: msg });
    }
  }, [batchName]);

  function onFileSelected(file: File) {
    setFileName(file.name);
    const reader = new FileReader();
    reader.onerror = () => setPhase({ kind: "error", message: "读取文件失败" });
    reader.onload = () => {
      const txt = String(reader.result ?? "");
      onSubmit(txt, file.name);
    };
    reader.readAsText(file, "utf-8");
  }

  function onDrop(e: React.DragEvent<HTMLDivElement>) {
    e.preventDefault();
    setDragging(false);
    const f = e.dataTransfer?.files?.[0];
    if (f) onFileSelected(f);
  }

  return (
    <div className="h-full overflow-y-auto bg-muted/30">
      <div className="border-b border-border bg-background px-6 py-3">
        <h1 className="text-lg font-semibold">CSV 批量导入</h1>
        <p className="text-xs text-muted-foreground">
          支持中英表头；单次上传最多 200 行；同一文件再次上传会复用已有批次。
        </p>
      </div>

      <div className="mx-auto max-w-4xl space-y-5 p-6">
        <div className="rounded-md border border-border bg-background p-4">
          <div className="mb-3 grid grid-cols-2 gap-3">
            <label className="block">
              <span className="mb-1 block text-xs text-muted-foreground">批次名称（可选）</span>
              <Input
                value={batchName}
                onChange={(e) => setBatchName(e.target.value)}
                placeholder="eg. 2026Q2 KOL 候选池"
              />
            </label>
            <label className="block">
              <span className="mb-1 block text-xs text-muted-foreground">文件名</span>
              <Input value={fileName} readOnly placeholder="拖拽后自动填充" />
            </label>
          </div>

          <div
            className={cn(
              "rounded-md border-2 border-dashed p-8 text-center transition-colors",
              dragging
                ? "border-primary bg-primary/5"
                : "border-border bg-muted/30",
            )}
            onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
            onDragLeave={() => setDragging(false)}
            onDrop={onDrop}
            onClick={() => fileInputRef.current?.click()}
            role="button"
            tabIndex={0}
          >
            <p className="text-sm">拖拽 CSV 文件到此处，或点击选择</p>
            <p className="mt-1 text-xs text-muted-foreground">
              第一行为表头（支持：url / channel_url / link / 链接 等）
            </p>
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv,text/csv,text/plain"
              className="hidden"
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) onFileSelected(f);
                e.target.value = "";
              }}
            />
          </div>
        </div>

        <PhasePanel phase={phase} onReset={() => setPhase({ kind: "idle" })} />
      </div>
    </div>
  );
}

function PhasePanel({ phase, onReset }: { phase: Phase; onReset: () => void }) {
  if (phase.kind === "idle") return null;

  if (phase.kind === "uploading") {
    return <Card>上传中…</Card>;
  }

  if (phase.kind === "error") {
    return (
      <Card>
        <p className="text-sm text-destructive">{phase.message}</p>
        <Button className="mt-2" size="sm" variant="outline" onClick={onReset}>
          重置
        </Button>
      </Card>
    );
  }

  // queued → look up batch by hash
  if (phase.kind === "queued") {
    return <QueuedPanel resp={phase.resp} hash={phase.hash} />;
  }

  // processing → poll batch
  if (phase.kind === "processing") {
    return <ProcessingPanel batchID={phase.batchID} onReset={onReset} />;
  }

  if (phase.kind === "done") {
    return <ProcessingPanel batchID={phase.batchID} onReset={onReset} />;
  }

  return null;
}

function Card({ children }: { children: React.ReactNode }) {
  return (
    <div className="rounded-md border border-border bg-background p-4 text-sm">
      {children}
    </div>
  );
}

function QueuedPanel({ resp, hash }: { resp: UploadResponse; hash: string }) {
  // Poll /api/csv/batches?by_file_hash until we see an ID, then switch to
  // the processing panel which polls the batch row.
  const lookupQ = useQuery({
    queryKey: ["csv-batch-by-hash", hash],
    queryFn: () => endpoints.csvBatchByHash(hash),
    refetchInterval: (q) => {
      const data = q.state.data;
      if (data && "id" in data) return false;
      return 1000;
    },
  });

  const batchID =
    lookupQ.data && "id" in lookupQ.data ? lookupQ.data.id : null;

  return (
    <Card>
      <div className="space-y-2">
        <p className="font-medium">上传已接收</p>
        <p className="text-xs text-muted-foreground">
          总行数：{resp.total_rows} · 服务端正在异步处理；批次行创建后会显示进度
        </p>
        {resp.unmatched_headers && resp.unmatched_headers.length > 0 && (
          <p className="text-xs text-amber-600">
            未识别表头：{resp.unmatched_headers.join(", ")}（将被忽略）
          </p>
        )}
        {batchID !== null ? (
          <p className="text-xs">
            批次 ID：
            <span className="font-mono">#{batchID}</span>
          </p>
        ) : (
          <p className="text-xs text-muted-foreground">等待批次落库…</p>
        )}
      </div>
      {batchID !== null && <ProcessingPanel batchID={batchID} onReset={() => location.reload()} />}
    </Card>
  );
}

function ProcessingPanel({ batchID, onReset }: { batchID: number; onReset: () => void }) {
  const batchQ = useQuery({
    queryKey: ["csv-batch", batchID],
    queryFn: () => endpoints.csvBatch(batchID),
    refetchInterval: (q) => {
      const s = q.state.data?.status;
      if (s === "completed" || s === "failed" || s === "partial") return false;
      return 1500;
    },
  });

  const itemsQ = useQuery({
    queryKey: ["csv-batch-items", batchID, batchQ.data?.status],
    queryFn: () => endpoints.csvBatchItems(batchID),
    enabled: !!batchQ.data,
  });

  const batch = batchQ.data;

  return (
    <div className="mt-3 space-y-3">
      {batch && <BatchSummary batch={batch} />}
      {itemsQ.data && itemsQ.data.length > 0 && <ItemsTable items={itemsQ.data} />}
      <div className="flex justify-end">
        <Button size="sm" variant="outline" onClick={onReset}>
          上传另一份
        </Button>
      </div>
    </div>
  );
}

function BatchSummary({ batch }: { batch: BatchResponse }) {
  const variant: "info" | "success" | "danger" | "warning" =
    batch.status === "completed"
      ? "success"
      : batch.status === "failed"
        ? "danger"
        : batch.status === "partial"
          ? "warning"
          : "info";
  const pct =
    batch.total_items > 0
      ? Math.round((batch.processed_items / batch.total_items) * 100)
      : 0;
  return (
    <div className="rounded-md border border-border bg-background p-3">
      <div className="flex items-center gap-2">
        <span className="font-medium">{batch.name ?? `批次 #${batch.id}`}</span>
        <Badge variant={variant}>{batch.status}</Badge>
        <span className="ml-auto text-xs text-muted-foreground">
          进度 {batch.processed_items}/{batch.total_items}
          {batch.failed_items > 0 && (
            <span className="ml-2 text-amber-600">失败 {batch.failed_items}</span>
          )}
        </span>
      </div>
      <div className="mt-2 h-2 rounded-full bg-muted">
        <div
          className="h-2 rounded-full bg-primary transition-[width] duration-300"
          style={{ width: `${pct}%` }}
        />
      </div>
      {batch.error_summary && (
        <p className="mt-2 text-xs text-destructive">{batch.error_summary}</p>
      )}
    </div>
  );
}

function ItemsTable({ items }: { items: BatchItem[] }) {
  return (
    <div className="rounded-md border border-border bg-background">
      <Table>
        <THead>
          <TR>
            <TH>行号</TH>
            <TH>解析 URL</TH>
            <TH>状态</TH>
            <TH>频道</TH>
            <TH>错误</TH>
          </TR>
        </THead>
        <TBody>
          {items.map((it) => (
            <TR key={it.ID}>
              <TD className="font-mono text-xs">{it.LineNumber ?? "—"}</TD>
              <TD className="max-w-[20rem] truncate text-xs text-muted-foreground">
                {it.ResolvedUrl ?? "—"}
              </TD>
              <TD>
                <Badge variant={itemVariant(it.Status)}>{it.Status}</Badge>
              </TD>
              <TD>
                {it.ChannelID ? (
                  <Link
                    to={`/channels/${it.ChannelID}`}
                    className="text-primary hover:underline"
                  >
                    #{it.ChannelID}
                  </Link>
                ) : (
                  <span className="text-xs text-muted-foreground">—</span>
                )}
              </TD>
              <TD className="text-xs text-destructive">
                {it.ErrorMessage ?? ""}
              </TD>
            </TR>
          ))}
        </TBody>
      </Table>
    </div>
  );
}

function itemVariant(s: string): "success" | "danger" | "warning" | "secondary" {
  switch (s) {
    case "completed":
      return "success";
    case "failed":
      return "danger";
    case "skipped":
      return "warning";
    default:
      return "secondary";
  }
}

